<template>
  <DialogRoot v-model:open="dialogOpen" @update:open="handleCloseBookTestDriveModal">
    <DialogPortal>
      <Transition enter-active-class="backdrop-enter" leave-active-class="backdrop-leave">
        <DialogOverlay v-if="dialogOpen" class="modal-backdrop" />
      </Transition>

      <Transition enter-active-class="modal-enter" leave-active-class="modal-leave">
        <DialogContent v-if="dialogOpen" class="modal-content">
          <DialogTitle as="div" class="mb-6 lg:mb-10 flex justify-between items-start">
            <h4 class="">{{ t('components.testDriveModal.title', { carTitle: currentCarDetail?.model }) }}</h4>

            <DialogClose asChild class="cursor-pointer size-6 min-w-6 mt-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="text-accent">
                <path
                  d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
                  fill="currentColor"
                />
              </svg>
            </DialogClose>
          </DialogTitle>

          <DialogDescription class="sr-only">
            {{ t('components.testDriveModal.description', { carTitle: currentCarDetail?.model }) }}
          </DialogDescription>

          <div class="book-test-drive__form h-full overflow-y-auto pr-2" v-if="!isSuccess">
            <div class="book-test-drive__card-data flex flex-col lg:grid lg:grid-cols-2 pb-3 border-b border-accent gap-6">
              <SharedPicture
                v-if="useVehicleImages(currentCarDetail ?? {})[0]"
                :media="useVehicleImages(currentCarDetail ?? {})[0]"
                class="object-cover object-center rounded-sm aspect-auto !w-full !h-auto max-w-[433px] !block lg:!hidden"
                imageClass="object-cover object-center block rounded-sm"
                height="323"
                width="433"
              />
              <SharedPicture
                v-if="useVehicleImages(currentCarDetail ?? {})[0]"
                :media="useVehicleImages(currentCarDetail ?? {})[0]"
                class="object-cover object-center rounded-sm !aspect-[312/233] !w-full !max-w-[312px] !h-auto !hidden lg:!block"
                imageClass="object-cover object-center block w-full h-auto  rounded-sm"
                width="312"
                height="233"
              />

              <div class="flex flex-col gap-2">
                <h6 class="">{{ currentCarDetail?.make?.name }} {{ currentCarDetail?.model }}</h6>
                <p class="text-medium-semibold mb-6">{{ carPrice }}</p>
                <p class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none">
                    <path
                      d="M8.00006 12C10.2061 12 12.0001 10.206 12.0001 8C12.0001 5.794 10.2061 4 8.00006 4C5.79406 4 4.00006 5.794 4.00006 8C4.00006 10.206 5.79406 12 8.00006 12ZM8.00006 6C9.10306 6 10.0001 6.897 10.0001 8C10.0001 9.103 9.10306 10 8.00006 10C6.89706 10 6.00006 9.103 6.00006 8C6.00006 6.897 6.89706 6 8.00006 6Z"
                      fill="#37B5AA"
                    />
                    <path
                      d="M7.42009 19.814C7.58934 19.9349 7.79211 19.9998 8.00009 19.9998C8.20806 19.9998 8.41084 19.9349 8.58009 19.814C8.88409 19.599 16.0291 14.44 16.0001 8C16.0001 3.589 12.4111 0 8.00009 0C3.58909 0 8.80377e-05 3.589 8.80377e-05 7.995C-0.028912 14.44 7.11609 19.599 7.42009 19.814ZM8.00009 2C11.3091 2 14.0001 4.691 14.0001 8.005C14.0211 12.443 9.61209 16.428 8.00009 17.735C6.38909 16.427 1.97909 12.441 2.00009 8C2.00009 4.691 4.69109 2 8.00009 2Z"
                      fill="#37B5AA"
                    />
                  </svg>
                  <span class="text-regular-semibold">{{ currentCarDetail?.location }}</span>
                </p>
              </div>
            </div>

            <div class="book-test-drive__form flex flex-col gap-10 lg:gap-16 mt-12 mb-10">
              <div class="book-test drive__date-selector flex flex-col gap-6 lg:gap-10">
                <div class="general-data__section-title flex items-start gap-4 mb-4">
                  <div class="size-8 bg-accent text-white rounded-full flex items-center justify-center">
                    <span class="text-center">1</span>
                  </div>
                  <p class="general-data__section-title-text text-medium-semibold">
                    {{ t('components.testDriveModal.form.dateSelector.title') }}
                  </p>
                </div>

                <CalendarRoot
                  v-slot="{ weekDays, grid }"
                  :is-date-unavailable="isDateUnavailable"
                  :locale="locale"
                  class=""
                  :number-of-months="1"
                  weekdayFormat="short"
                  disableDaysOutsideCurrentView
                  v-model="serviceDate"
                  :min-value="today(getLocalTimeZone()) as any"
                >
                  <CalendarHeader class="flex items-center justify-between mb-6">
                    <CalendarPrev class="">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path
                          d="M12 2.5C6.486 2.5 2 6.986 2 12.5C2 18.014 6.486 22.5 12 22.5C17.514 22.5 22 18.014 22 12.5C22 6.986 17.514 2.5 12 2.5ZM12 20.5C7.589 20.5 4 16.911 4 12.5C4 8.089 7.589 4.5 12 4.5C16.411 4.5 20 8.089 20 12.5C20 16.911 16.411 20.5 12 20.5Z"
                          fill="#1A847B"
                        />
                        <path d="M13.293 6.79303L7.586 12.5L13.293 18.207L14.707 16.793L10.414 12.5L14.707 8.20703L13.293 6.79303Z" fill="#1A847B" />
                      </svg>
                    </CalendarPrev>
                    <CalendarHeading as="h4" class="text-center !text-2xl" />

                    <CalendarNext class="">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path
                          d="M12 2.5C6.486 2.5 2 6.986 2 12.5C2 18.014 6.486 22.5 12 22.5C17.514 22.5 22 18.014 22 12.5C22 6.986 17.514 2.5 12 2.5ZM12 20.5C7.589 20.5 4 16.911 4 12.5C4 8.089 7.589 4.5 12 4.5C16.411 4.5 20 8.089 20 12.5C20 16.911 16.411 20.5 12 20.5Z"
                          fill="#1A847B"
                        />
                        <path d="M9.29303 8.20703L13.586 12.5L9.29303 16.793L10.707 18.207L16.414 12.5L10.707 6.79303L9.29303 8.20703Z" fill="#1A847B" />
                      </svg>
                    </CalendarNext>
                  </CalendarHeader>

                  <div class="">
                    <CalendarGrid v-for="month in grid" :key="month.value.toString()" class="w-full border-collapse select-none space-y-1">
                      <CalendarGridHead class="border-b border-b-accent">
                        <CalendarGridRow class="grid w-full grid-cols-7">
                          <CalendarHeadCell v-for="day in weekDays" :key="day">
                            <h5 class="py-3 pb-5 !text-xl text-center">
                              {{ day }}
                            </h5>
                          </CalendarHeadCell>
                        </CalendarGridRow>
                      </CalendarGridHead>
                      <CalendarGridBody class="grid gap-3 pt-6">
                        <CalendarGridRow v-for="(weekDates, index) in month.rows" :key="`weekDate-${index}`" class="grid grid-cols-7">
                          <CalendarCell v-for="weekDate in weekDates" :key="weekDate.toString()" :date="weekDate" class="flex items-center justify-center">
                            <CalendarCellTrigger :day="weekDate" :month="month.value" class="date-cell" :class="{}">
                              <h4 class="!text-2xl text-center">
                                {{ weekDate.day }}
                              </h4>
                            </CalendarCellTrigger>
                            <!-- <CalendarCellTrigger
                      :day="weekDate"
                      :month="month.value"
                      class="relative flex items-center justify-center rounded-full whitespace-nowrap text-sm font-normal text-black w-8 h-8 outline-none focus:shadow-[0_0_0_2px] focus:shadow-black data-[outside-view]:text-black/30 data-[selected]:!bg-green10 data-[selected]:text-white hover:bg-green5 data-[highlighted]:bg-green5 data-[unavailable]:pointer-events-none data-[unavailable]:text-black/30 data-[unavailable]:line-through before:absolute before:top-[5px] before:hidden before:rounded-full before:w-1 before:h-1 before:bg-white data-[today]:before:block data-[today]:before:bg-green9"
                    /> -->
                          </CalendarCell>
                        </CalendarGridRow>
                      </CalendarGridBody>
                    </CalendarGrid>
                    <p class="checkbox__error text-small-normal !text-[#CF2402] mt-4" v-if="isFieldTouched('serviceDate') && errors.serviceDate">
                      {{ errors.serviceDate }}
                    </p>
                  </div>
                </CalendarRoot>
              </div>

              <div class="book-test drive__date-selector flex flex-col gap-6 lg:gap-10">
                <div class="general-data__section-title flex items-start gap-4 mb-4">
                  <div class="size-8 bg-accent text-white rounded-full flex items-center justify-center">
                    <span class="text-center">2</span>
                  </div>
                  <p class="general-data__section-title-text text-medium-semibold">
                    {{ t('components.testDriveModal.form.timeSelector.title') }}
                  </p>
                </div>
                <div class="book-test-drive__data__section-content book-test-drive__data__section-content--service-time flex flex-col gap-4">
                  <div class="grid grid-cols-4 gap-3">
                    <button
                      v-for="serviceTime in serviceTimeOptions"
                      :key="serviceTime.value"
                      :title="serviceTime.label"
                      class="bg-white rounded-sm border border-neutral-lighter p-2 py-3 text-regular-semibold hover:cursor-pointer hover:bg-accent/30 hover:border-accent hover:text-neutral-darker"
                      :disabled="!serviceTime.available"
                      @click="() => serviceTime.available && setFieldValue('serviceTime', serviceTime.value)"
                      :class="{
                        '!border-accent !bg-accent !text-white': serviceTime.value === values.serviceTime,
                        '!bg-neutral-lighter !border-neutral-lighter !text-neutral-darker !cursor-not-allowed': !serviceTime.available,
                      }"
                    >
                      {{ serviceTime.label }}
                    </button>
                  </div>
                  <div class="flex items-center gap-2 hover:cursor-pointer" @click="showMoreTimes = true" v-if="!showMoreTimes">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M14 6H8V0H6V6H0V8H6V14H8V8H14V6Z" fill="#1A847B" />
                    </svg>
                    <span class="font-semibold text-base text-primary leading-[150%]">{{ t('components.workshopBooking.generalDataForm.thirdSection.moreTimes') }}</span>
                  </div>

                  <p class="checkbox__error text-small-normal !text-[#CF2402] mt-4" v-if="isFieldTouched('serviceTime') && errors.serviceTime">
                    {{ errors.serviceTime }}
                  </p>
                </div>
              </div>

              <div class="book-test drive__date-selector flex flex-col gap-6 lg:gap-10">
                <div class="general-data__section-title flex items-start gap-4 mb-4">
                  <div class="size-8 bg-accent text-white rounded-full flex items-center justify-center">
                    <span class="text-center">3</span>
                  </div>
                  <p class="general-data__section-title-text text-medium-semibold">
                    {{ t('components.testDriveModal.form.contactSelector.title') }}
                  </p>
                </div>

                <div class="personal-data__form flex flex-col gap-4 lg:grid lg:grid-cols-2">
                  <div class="form-control form-control__row">
                    <SharedInput
                      :label="t('components.workshopBooking.personalDataForm.firstName')"
                      :id="`${compoentId}-firstName`"
                      name="firstName"
                      type="text"
                      required
                      v-bind="firstNameAttrs"
                      v-model="firstName"
                      :error="isFieldTouched('firstName') ? errors.firstName : undefined"
                      @blur="setFieldTouched('firstName', true)"
                    />
                  </div>

                  <div class="form-control form-control__row">
                    <SharedInput
                      :label="t('components.workshopBooking.personalDataForm.lastName')"
                      :id="`${compoentId}-lastName`"
                      name="lastName"
                      type="text"
                      required
                      v-bind="lastNameAttrs"
                      v-model="lastName"
                      :error="isFieldTouched('lastName') ? errors.lastName : undefined"
                      @blur="setFieldTouched('lastName', true)"
                    />
                  </div>

                  <div class="form-control form-control__row">
                    <SharedInput
                      :label="t('components.workshopBooking.personalDataForm.email')"
                      :id="`${compoentId}-email`"
                      name="email"
                      type="email"
                      required
                      v-bind="emailAttrs"
                      v-model="email"
                      :error="isFieldTouched('email') ? errors.email : undefined"
                      @blur="setFieldTouched('email', true)"
                    />
                  </div>

                  <div class="form-control form-control__row">
                    <SharedInput
                      :label="t('components.workshopBooking.personalDataForm.phone')"
                      :id="`${compoentId}-phone`"
                      name="phone"
                      type="tel"
                      v-bind="phoneAttrs"
                      v-model="phone"
                      required
                      :error="isFieldTouched('phone') ? errors.phone : undefined"
                      @blur="setFieldTouched('phone', true)"
                    />
                  </div>

                  <div class="form-control form-control__row lg:col-span-2">
                    <SharedInput
                      :label="t('components.testDriveModal.form.bestReachable')"
                      :id="`${compoentId}-bestReachable`"
                      name="bestReachable"
                      type="text"
                      v-bind="bestReachableAttrs"
                      v-model="bestReachable"
                      :error="isFieldTouched('bestReachable') ? errors.bestReachable : undefined"
                      @blur="setFieldTouched('bestReachable', true)"
                    />
                  </div>

                  <div class="flex flex-col gap-4 lg:col-span-2">
                    <SharedCheckbox :id="`${compoentId}-acceptTerms`" name="acceptTerms" v-model="acceptTermsTSCorrect" required :error="errors.acceptTerms">
                      <i18n-t keypath="components.workshopBooking.generalDataForm.thirdSection.acceptTerms" scope="global">
                        <NuxtLink
                          class="text-small-normal underline"
                          :target="'_blank'"
                          rel="noopener noreferrer"
                          :to="`${dataProtectionPolicyUrl}`"
                          :title="siteOptions?.data?.dataProtectionPolicy?.title"
                        >
                          {{ t('components.workshopBooking.generalDataForm.thirdSection.acceptedTermsLink') }}
                        </NuxtLink>
                      </i18n-t>
                    </SharedCheckbox>
                    <!-- <SharedCheckbox :id="`${compoentId}-subscribeToNewsletter`" name="subscribeToNewsletter" v-model="subscribeToNewsletterTSCorrect">
                      <span class="text-small-normal">{{ t('components.workshopBooking.generalDataForm.thirdSection.subscribeToNewsletter') }}</span>
                    </SharedCheckbox> -->
                  </div>
                </div>
                <NuxtTurnstile v-model="turnstileToken" v-if="firstTimeSubmitted && !validTurnstileToken" />
                <div class="personal-data__actions flex flex-col-reverse md:flex-row justify-between gap-4">
                  <SharedButton
                    @click="onSubmit"
                    class="w-max"
                    :disabled="isSubmitting || (meta.touched && !meta.valid)"
                    :label="t('components.workshopBooking.generalDataForm.submit')"
                  />
                </div>
              </div>
            </div>
            <div v-if="formSubmittedError" class="form-submitted-error">
              <p class="text-small-normal !text-[#CF2402]">{{ t('components.testDriveModal.form.errorMessage') }}</p>
            </div>
          </div>

          <div class="book-test-drive__success h-full overflow-y-auto text-medium-normal" v-else>
            <p>{{ t('components.testDriveModal.success.title') }}</p>
            <br />
            <p class="text-medium-semibold">{{ currentCarDetail?.make?.name }} {{ currentCarDetail?.model }}</p>
            <p>{{ t('components.testDriveModal.success.location', { location: currentCarDetail?.location }) }}</p>
            <br />
            <p>{{ t('components.testDriveModal.success.at') }}</p>
            <p class="text-medium-semibold">{{ formattedServiceDate }}, {{ t('components.testDriveModal.success.atTime') }} {{ values.serviceTime }}</p>
            <br />
            <p>{{ t('components.testDriveModal.success.endText') }}</p>
            <div class="flex flex-wrap gap-4 mt-10 justify-between">
              <SharedButton :label="t('components.testDriveModal.success.close')" @click="handleCloseBookTestDriveModal(false)" />
            </div>
          </div>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  import type { GraphQLCarDetail } from '~/types/graphql';
  import type { CalendarRootProps } from 'reka-ui';
  import type { StrapiResponse, SiteOption, TestdriveBooking } from '~/types/strapi';
  import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
  // import { Field, Form } from 'vee-validate';
  import { today, getLocalTimeZone } from '@internationalized/date';
  import {
    WEEKDAY_SLOTS,
    SATURDAY_SLOTS,
    WEEKDAY_INITIAL_COUNT,
    SATURDAY_INITIAL_COUNT,
    getCalendarDayOfWeek,
  } from '#shared/booking-slots';
  import { z } from 'zod';
  import { toTypedSchema } from '@vee-validate/zod';
  import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';

  interface EventData {
    event: 'book_test_drive_form_started' | 'book_test_drive_form_submitted' | 'book_test_drive_form_error';
    page_location: string;
    page_title: string;
    vehicle_id: string;
    make?: string;
    model?: string;
    manufactured_at?: string;
    price?: number;
    fuel_type?: string;
    user_data?: {
      sha256_email_address?: string;
      sha256_phone_number?: string;
    };
  }

  const { push } = useDataLayer();

  const compoentId = useId();
  const siteOptions = inject('siteOptions') as Ref<StrapiResponse<SiteOption>> | undefined;
  const dataProtectionPolicyUrl = computed(() => useLocalizedPath(siteOptions?.value?.data?.dataProtectionPolicy?.slug || ''));

  // const isError = ref(false);
  // const isSuccess = ref(false);

  const { lockScroll, unlockScroll } = useScrollLock();
  const currentCarDetail = ref<GraphQLCarDetail | null>(null);
  const eventBus = useEventBus();

  const pageCarDetail = inject<GraphQLCarDetail>('carDetail');

  const handleOpenBookTestDriveModal = (carDetail?: { carDetail: GraphQLCarDetail }) => {
    dialogOpen.value = true;
    if (carDetail) {
      currentCarDetail.value = carDetail.carDetail;
    } else {
      currentCarDetail.value = pageCarDetail || null;
    }
    lockScroll();
  };

  const handleCloseBookTestDriveModal = (v: boolean) => {
    if (!v) {
      resetForm();
      isSuccess.value = false;
      dialogOpen.value = false;
      unlockScroll();
      currentCarDetail.value = null;
    }
  };

  eventBus.on('open-book-test-drive-modal', handleOpenBookTestDriveModal);

  const { t, locale } = useI18n();

  // Create a computed property for the open state that can be used with v-model
  const dialogOpen = ref(false);

  const carPrice = computed(() => {
    return `€ ${currentCarDetail.value?.asking_price?.toLocaleString(locale.value, {
      currency: 'EUR',
      minimumFractionDigits: 0,
    })}`;
  });

  const formattedServiceDate = computed(() => {
    const dateValue = values.serviceDate as any;
    if (!dateValue) return '';

    const dateObj = new Date(dateValue);
    if (Number.isNaN(dateObj.getTime())) {
      return dateValue?.toString?.() || '';
    }

    return dateObj.toLocaleDateString(locale.value || 'de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  });

  const isDateUnavailable: CalendarRootProps['isDateUnavailable'] = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateObj = new Date(date.year, date.month - 1, date.day);
    dateObj.setHours(0, 0, 0, 0);

    // Block only past days; allow today and future weekdays
    if (dateObj < today) return true;

    // Block Sundays (day 0)
    if (dateObj.getDay() === 0) return true;

    return false;
  };

  const schema = toTypedSchema(
    z.object({
      serviceDate: z.custom(val => val && val.toString().length > 0, t('validation.requiredDate')),
      serviceTime: z.string({ required_error: t('validation.requiredTime') }).min(1, t('validation.requiredTime')),
      firstName: z.string({ required_error: t('validation.required') }).min(1, t('validation.required')),
      lastName: z.string({ required_error: t('validation.required') }).min(1, t('validation.required')),
      email: z
        .string({ required_error: t('validation.required') })
        .email(t('validation.email'))
        .min(1, t('validation.required')),
      phone: z
        .string()
        .min(1, t('validation.required'))
        .refine(val => isValidPhoneNumber(val, { defaultCountry: locale.value.toUpperCase() as any }), t('validation.phoneNumber')),
      bestReachable: z.string().optional(),
      acceptTerms: z.boolean({ required_error: t('validation.required') }).refine(val => val, t('validation.required')),
      subscribeToNewsletter: z.coerce.boolean(),
    })
  );

  const { values, errors, meta, setFieldValue, setFieldTouched, isFieldTouched, defineField, resetForm, setTouched, validate } = useForm({
    validationSchema: schema,
  });

  const [serviceDate, serviceDateAttrs] = defineField('serviceDate');
  const [serviceTime, serviceTimeAttrs] = defineField('serviceTime');
  const [firstName, firstNameAttrs] = defineField('firstName');
  const [lastName, lastNameAttrs] = defineField('lastName');
  const [email, emailAttrs] = defineField('email');
  const [phone, phoneAttrs] = defineField('phone');
  const [bestReachable, bestReachableAttrs] = defineField('bestReachable');
  const [acceptTerms, acceptTermsAttrs] = defineField('acceptTerms');
  const [subscribeToNewsletter, subscribeToNewsletterAttrs] = defineField('subscribeToNewsletter');
  const showMoreTimes = ref(false);

  const acceptTermsTSCorrect = computed<boolean>({
    get() {
      let v = acceptTerms.value || false;
      return v as boolean;
    },
    set(value: boolean) {
      setFieldValue('acceptTerms', value);
    },
  });

  const subscribeToNewsletterTSCorrect = computed<boolean>({
    get() {
      let v = subscribeToNewsletter.value || false;
      return v as boolean;
    },
    set(value: boolean) {
      setFieldValue('subscribeToNewsletter', value);
    },
  });

  watch(serviceDate, () => {
    setFieldValue('serviceTime', '');
    showMoreTimes.value = false;
  });

  const isDateToday = (date?: { calendar: string; era: 'AD' | 'BC'; year: number; month: number; day: number }) => {
    if (!date) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateObj = new Date(date.year, date.month - 1, date.day, 0, 0, 0, 0);

    return dateObj.getTime() === today.getTime();
  };

  const roundToNearestHalfHour = (input: Date) => {
    const rounded = new Date(input);
    const minutes = rounded.getMinutes();

    if (minutes < 15) {
      rounded.setMinutes(0, 0, 0);
    } else if (minutes < 45) {
      rounded.setMinutes(30, 0, 0);
    } else {
      rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
    }

    return rounded;
  };

  const isTimeAvailable = (hours: string, date?: string) => {
    if (!date) return true;

    if (!isDateToday(date as any)) return true;

    // Round current time to the nearest half-hour mark (00 or 30)
    const roundedNow = roundToNearestHalfHour(new Date());

    const hoursDate = new Date(date);
    const [hrs, mins] = hours.split(':').map(part => parseInt(part || '0', 10));
    hoursDate.setHours(hrs || 0, mins || 0, 0, 0);

    // Allow only times at least 3 hours after the rounded current time
    return hoursDate.getTime() > roundedNow.getTime() + 3 * 60 * 60 * 1000;
  };

  const serviceTimeOptions = computed(() => {
    const isSaturday = getCalendarDayOfWeek(serviceDate.value as any) === 6;
    const allSlots = isSaturday ? SATURDAY_SLOTS : WEEKDAY_SLOTS;
    const initialCount = isSaturday ? SATURDAY_INITIAL_COUNT : WEEKDAY_INITIAL_COUNT;
    const visibleSlots = showMoreTimes.value ? allSlots : allSlots.slice(0, initialCount);

    return visibleSlots.map((value: string) => ({
      label: value,
      value,
      available: isTimeAvailable(value, serviceDate.value),
    }));
  });

  const isSubmitting = ref(false);

  const formSubmittedError = ref(false);
  const firstTimeSubmitted = ref(false);
  const isSuccess = ref(false);

  const event = ref<TestdriveBooking | null>(null);

  const onSubmit = async () => {
    if (!firstTimeSubmitted.value) {
      firstTimeSubmitted.value = true;
    }

    if (isSubmitting.value || !validTurnstileToken.value) {
      return;
    }

    formSubmittedError.value = false;

    isSubmitting.value = true;

    setTouched(true);
    const { valid } = await validate();
    if (!valid) {
      isSubmitting.value = false;
      return;
    }

    if (!currentCarDetail.value?.documentId) {
      formSubmittedError.value = true;
      return;
    }

    let success = false;
    try {
      const eventData: EventData = {
        event: 'book_test_drive_form_submitted',
        page_location: window.location.href,
        page_title: window.document.title,
        vehicle_id: currentCarDetail.value?.internal_vehicle_no?.toString() || '',
        make: currentCarDetail.value?.make?.name,
        model: currentCarDetail.value?.model,
        manufactured_at: currentCarDetail.value?.year_of_manufacture?.toString() || '',
        price: currentCarDetail.value?.asking_price,
        fuel_type: currentCarDetail.value?.fuel_type,
        user_data: {
          sha256_email_address: await useShaHash(values.email!),
          sha256_phone_number: await useShaHash(values.phone!),
        },
      };
      push(eventData);

      const response = await $fetch<TestdriveBooking>('/api/book-test-drive', {
        method: 'POST',
        body: { ...values, language: locale.value, carId: currentCarDetail.value.documentId },
      });

      if (!response) {
        formSubmittedError.value = true;
        return;
      }

      event.value = response;
      success = true;
    } catch (err) {
      success = false;
    }

    nextTick(() => {
      // wait for the next tick to ensure the form is updated
      if (success) {
        isSubmitting.value = false;
        isSuccess.value = true;
      } else {
        isSubmitting.value = false;
        isSuccess.value = false;
        formSubmittedError.value = true;
      }
    });
  };
  const { validTurnstileToken, turnstileToken } = useValidTurnstileTokenCookie(onSubmit);

  watch(meta, (newMeta, oldMeta) => {
    if (newMeta.touched && !oldMeta?.touched) {
      const eventData: EventData = {
        event: 'book_test_drive_form_started',
        page_location: window.location.href,
        page_title: window.document.title,
        vehicle_id: currentCarDetail.value?.internal_vehicle_no?.toString() || '',
        make: currentCarDetail.value?.make?.name,
        model: currentCarDetail.value?.model,
        manufactured_at: currentCarDetail.value?.year_of_manufacture?.toString() || '',
        price: currentCarDetail.value?.asking_price,
        fuel_type: currentCarDetail.value?.fuel_type,
      };
      push(eventData);
    }
  });
</script>

<style lang="scss" scoped>
  .date-cell {
    @apply h-[55px] w-[45px] rounded-sm border border-neutral-lighter;
    @apply bg-accent/40 flex items-center justify-center hover:bg-accent/15;
    @screen sm {
      @apply size-[55px];
    }

    &[data-unavailable] {
      @apply bg-white text-neutral-darker cursor-not-allowed;
    }
    &[data-outside-visible-view] {
      @apply hidden;
    }
    &[data-selected] {
      @apply bg-accent text-white;
    }
  }
</style>
